import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  Modal,
  ActivityIndicator,
  SafeAreaView,
} from 'react-native';
import { useRouter } from 'expo-router';
import { activitiesApi, goalsApi, visionsApi, Activity, Goal, Vision } from '../api/client';
import { decryptTitle, decryptEntityTitle } from '../utils/encryption';
import Icon, { ICON_SIZES } from './Icon';
import { useTheme } from '../theme/ThemeContext';
import { useThemedStyles } from '../theme/useThemedStyles';
import type { Theme } from '../theme/types';

interface SearchModalProps {
  visible: boolean;
  onClose: () => void;
}

const TYPE_TAB_MAP: Record<string, string> = {
  activity: '/(tabs)/activities',
  goal: '/(tabs)/goals',
  vision: '/(tabs)/visions',
};

const SECTION_ORDER = ['activity', 'goal', 'vision'];

function HighlightedText({ text, query, styles }: { text: string; query: string; styles: ReturnType<typeof createStyles> }) {
  if (!query.trim()) {
    return <Text style={styles.resultTitle} numberOfLines={1}>{text}</Text>;
  }
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <Text style={styles.resultTitle} numberOfLines={1}>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <Text key={i} style={styles.highlightedText}>{part}</Text>
        ) : (
          <Text key={i}>{part}</Text>
        )
      )}
    </Text>
  );
}

interface DecryptedEntity {
  id: string;
  type: 'activity' | 'goal' | 'vision';
  title: string;
}

interface DisplayResult {
  entity_type: string;
  entity_id: string;
  title: string;
}

export default function SearchModal({ visible, onClose }: SearchModalProps) {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<DisplayResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<TextInput>(null);
  const [allEntities, setAllEntities] = useState<DecryptedEntity[]>([]);
  const [entitiesLoaded, setEntitiesLoaded] = useState(false);
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  // Load and decrypt all entities when modal opens
  useEffect(() => {
    if (!visible) {
      setEntitiesLoaded(false);
      setAllEntities([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [tRes, gRes, vRes] = await Promise.all([
          activitiesApi.list(),
          goalsApi.list(),
          visionsApi.list(),
        ]);
        const activities = tRes.data;
        const goals = gRes.data;
        const visions = vRes.data;

        const entities: DecryptedEntity[] = await Promise.all([
          ...activities.map(async (t): Promise<DecryptedEntity> => ({ id: t.id, type: 'activity', title: await decryptEntityTitle(t) })),
          ...goals.map(async (g): Promise<DecryptedEntity> => ({ id: g.id, type: 'goal', title: await decryptEntityTitle(g) })),
          ...visions.map(async (v): Promise<DecryptedEntity> => ({ id: v.id, type: 'vision', title: await decryptEntityTitle(v) })),
        ]);
        if (!cancelled) {
          setAllEntities(entities);
          setEntitiesLoaded(true);
        }
      } catch {
        if (!cancelled) setEntitiesLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [visible]);

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Client-side search
  useEffect(() => {
    if (!debouncedQuery || !entitiesLoaded) {
      setResults([]);
      setHasSearched(!!debouncedQuery);
      setIsLoading(!!debouncedQuery && !entitiesLoaded);
      return;
    }

    setIsLoading(false);
    setHasSearched(true);

    const lowerQ = debouncedQuery.toLowerCase();
    const matched = allEntities
      .filter((e) => e.title.toLowerCase().includes(lowerQ))
      .map((e) => ({
        entity_type: e.type,
        entity_id: e.id,
        title: e.title,
      }));
    setResults(matched);
  }, [debouncedQuery, entitiesLoaded, allEntities]);

  // Focus input when modal opens
  useEffect(() => {
    if (visible) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery('');
      setDebouncedQuery('');
      setResults([]);
      setHasSearched(false);
    }
  }, [visible]);

  const handleResultPress = (item: DisplayResult) => {
    onClose();
    const path = TYPE_TAB_MAP[item.entity_type];
    if (path) {
      router.push(`${path}/${item.entity_id}` as any);
    }
  };

  // Group results by type
  const grouped = results.reduce(
    (acc, item) => {
      const type = item.entity_type;
      if (!acc[type]) acc[type] = [];
      acc[type].push(item);
      return acc;
    },
    {} as Record<string, DisplayResult[]>
  );

  // Build sections - always show all 3 entity types when searched (GAP #40)
  const sections = hasSearched && !isLoading
    ? SECTION_ORDER.flatMap((type) => {
        const items = grouped[type] || [];
        const count = items.length;
        return [
          { type: 'header' as const, key: `header-${type}`, title: `${type}s`, count },
          ...items.map((item) => ({ type: 'result' as const, key: item.entity_id, item, count: 0 })),
          ...(count === 0
            ? [{ type: 'empty' as const, key: `empty-${type}`, title: `No ${type}s found`, count: 0 }]
            : []),
        ];
      })
    : [];

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose} accessibilityViewIsModal={true}>
      <SafeAreaView style={styles.container} testID="search-modal">
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.searchInputContainer}>
            <Icon name="search-outline" size={ICON_SIZES.inline} color={colors.textMuted} style={{ marginRight: 8 }} />
            <TextInput
              ref={inputRef}
              style={styles.searchInput}
              placeholder="Search activities, goals, and visions..."
              placeholderTextColor={colors.placeholder}
              value={query}
              onChangeText={setQuery}
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType="search"
              accessibilityLabel="Search activities, goals, and visions"
              testID="search-modal.search-input"
            />
          </View>
          <TouchableOpacity
            onPress={onClose}
            style={styles.cancelButton}
            accessibilityRole="button"
            accessibilityLabel="Cancel search"
            testID="search-modal.cancel-button"
          >
            <Text style={styles.cancelText} accessible={false}>Cancel</Text>
          </TouchableOpacity>
        </View>

        {/* E2EE Info Banner (GAP #36) */}
        <View style={styles.e2eeBanner}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <Icon name="lock-closed" size={12} color={colors.primaryDark} />
            <Text style={styles.e2eeBannerText}>
              Searching local data only (end-to-end encrypted)
            </Text>
          </View>
        </View>

        {/* Results */}
        {!debouncedQuery && !hasSearched && (
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>Search activities, goals, and visions...</Text>
          </View>
        )}

        {isLoading && (
          <View style={styles.emptyState}>
            <ActivityIndicator size="small" color={colors.primary} accessibilityLabel="Searching" />
            <Text style={styles.emptyText}>Searching...</Text>
          </View>
        )}

        {!isLoading && hasSearched && results.length === 0 && (
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>No results found for "{debouncedQuery}"</Text>
          </View>
        )}

        {!isLoading && hasSearched && sections.length > 0 && (
          <FlatList
            data={sections}
            keyExtractor={(item) => item.key}
            renderItem={({ item }) => {
              if (item.type === 'header') {
                return (
                  <View style={styles.sectionHeaderRow}>
                    <Text style={styles.sectionHeader}>
                      {item.title.toUpperCase()}
                    </Text>
                    <Text style={styles.sectionCount}>
                      ({item.count} {item.count === 1 ? 'result' : 'results'})
                    </Text>
                  </View>
                );
              }
              if (item.type === 'empty') {
                return (
                  <View style={styles.emptySection}>
                    <Text style={styles.emptySectionText}>{item.title}</Text>
                  </View>
                );
              }
              const entityType = item.item.entity_type as 'activity' | 'goal' | 'vision';
              const typeColor = colors[entityType] || {
                bg: colors.bgTertiary,
                text: colors.textSecondary,
              };
              return (
                <TouchableOpacity
                  style={styles.resultItem}
                  onPress={() => handleResultPress(item.item)}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel={`${item.item.title}, ${item.item.entity_type}`}
                  testID={`search-modal.result-${item.item.entity_id}`}
                >
                  <View style={styles.resultContent} accessible={false}>
                    <HighlightedText text={item.item.title} query={debouncedQuery} styles={styles} />
                    {/* GAP #39: Show goal name and date for activity results */}
                    {item.item.entity_type === 'activity' && (item.item as any).goal_name && (
                      <Text style={styles.resultMeta} numberOfLines={1} accessible={false}>
                        {'\u{1F3AF}'} {(item.item as any).goal_name}
                        {(item.item as any).date ? ` \u00B7 ${(item.item as any).date}` : ''}
                      </Text>
                    )}
                  </View>
                  <View style={[styles.typeBadge, { backgroundColor: typeColor.bg }]} accessible={false}>
                    <Text style={[styles.typeBadgeText, { color: typeColor.text }]} accessible={false}>
                      {item.item.entity_type}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            }}
            contentContainerStyle={styles.listContent}
          />
        )}
      </SafeAreaView>
    </Modal>
  );
}

const createStyles = (theme: Theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    gap: 8,
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    backgroundColor: theme.colors.bgTertiary,
    borderRadius: 10,
    paddingHorizontal: 12,
    height: 40,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    color: theme.colors.textPrimary,
    padding: 0,
  },
  cancelButton: {
    paddingHorizontal: 4,
    paddingVertical: 8,
  },
  cancelText: {
    fontSize: 15,
    color: theme.colors.primary,
    fontWeight: '500' as const,
  },
  e2eeBanner: {
    backgroundColor: theme.colors.primaryBg,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.primaryLight,
  },
  e2eeBannerText: {
    fontSize: 12,
    color: theme.colors.primaryDark,
    fontWeight: '500' as const,
    textAlign: 'center' as const,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 24,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
    color: theme.colors.textDisabled,
    textAlign: 'center' as const,
  },
  listContent: {
    paddingVertical: 8,
  },
  sectionHeaderRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 4,
    gap: 6,
  },
  sectionHeader: {
    fontSize: 11,
    fontWeight: '700' as const,
    color: theme.colors.textDisabled,
    letterSpacing: 0.5,
  },
  sectionCount: {
    fontSize: 11,
    fontWeight: '500' as const,
    color: theme.colors.borderMedium,
  },
  emptySection: {
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  emptySectionText: {
    fontSize: 13,
    color: theme.colors.borderMedium,
    fontStyle: 'italic' as const,
  },
  resultItem: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  resultContent: {
    flex: 1,
  },
  resultTitle: {
    fontSize: 15,
    color: theme.colors.textPrimary,
  },
  highlightedText: {
    fontWeight: '700' as const,
    backgroundColor: theme.colors.highlightBg,
  },
  resultMeta: {
    fontSize: 12,
    color: theme.colors.textDisabled,
    marginTop: 2,
  },
  typeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    marginLeft: 8,
  },
  typeBadgeText: {
    fontSize: 11,
    fontWeight: '600' as const,
  },
});
